import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTrendingUp, FiTrendingDown, FiCalendar, FiPercent } = FiIcons;

const ProfitSummary = ({ profitData, periodType }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Adjust the displayed data based on the period
  const displayData = {
    daily: {
      current: profitData.daily,
      weekly: profitData.daily * 7,
      monthly: profitData.daily * 30,
      period: 'Daily'
    },
    weekly: {
      current: profitData.weekly,
      daily: profitData.weekly / 7,
      monthly: profitData.weekly * 4.3,
      period: 'Weekly'
    },
    monthly: {
      current: profitData.monthly,
      daily: profitData.monthly / 30,
      weekly: profitData.monthly / 4.3,
      period: 'Monthly'
    }
  }[periodType];

  const summaryCards = [
    {
      title: `${displayData.period} Profit`,
      value: displayData.current,
      icon: FiTrendingUp,
      period: `per ${periodType.slice(0, -2)}`,
      color: displayData.current >= 0 ? 'from-green-500 to-emerald-600' : 'from-red-500 to-red-600'
    },
    {
      title: periodType === 'daily' ? 'Weekly Profit' : periodType === 'weekly' ? 'Monthly Profit' : 'Daily Profit',
      value: periodType === 'daily' ? displayData.weekly : periodType === 'weekly' ? displayData.monthly : displayData.daily,
      icon: FiCalendar,
      period: periodType === 'daily' ? 'per week' : periodType === 'weekly' ? 'per month' : 'per day',
      color: (periodType === 'daily' ? displayData.weekly : periodType === 'weekly' ? displayData.monthly : displayData.daily) >= 0 
        ? 'from-blue-500 to-blue-600' 
        : 'from-red-500 to-red-600'
    },
    {
      title: periodType === 'daily' ? 'Monthly Profit' : periodType === 'weekly' ? 'Daily Profit' : 'Weekly Profit',
      value: periodType === 'daily' ? displayData.monthly : periodType === 'weekly' ? displayData.daily : displayData.weekly,
      icon: FiTrendingUp,
      period: periodType === 'daily' ? 'per month' : periodType === 'weekly' ? 'per day' : 'per week',
      color: (periodType === 'daily' ? displayData.monthly : periodType === 'weekly' ? displayData.daily : displayData.weekly) >= 0 
        ? 'from-purple-500 to-purple-600' 
        : 'from-red-500 to-red-600'
    },
    {
      title: 'Profit Margin',
      value: profitData.margin,
      icon: FiPercent,
      period: 'margin',
      color: profitData.margin >= 15 
        ? 'from-green-500 to-emerald-600' 
        : profitData.margin >= 5 
        ? 'from-yellow-500 to-orange-500' 
        : 'from-red-500 to-red-600',
      isPercentage: true
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
      className="bg-white rounded-3xl shadow-xl p-8 border border-slate-200"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-2 rounded-xl">
          <SafeIcon icon={FiTrendingUp} className="text-xl text-white" />
        </div>
        <h3 className="text-2xl font-bold text-slate-800">Profit Summary</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {summaryCards.map((card, index) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.1 * index }}
            className="relative group"
          >
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6 border-2 border-slate-200 hover:border-slate-300 transition-all duration-200 hover:shadow-lg">
              <div className="flex items-center justify-between mb-3">
                <div className={`bg-gradient-to-r ${card.color} p-2 rounded-lg shadow-sm`}>
                  <SafeIcon icon={card.icon} className="text-lg text-white" />
                </div>
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  {card.period}
                </span>
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-semibold text-slate-600">{card.title}</h4>
                <p className={`text-2xl font-bold ${
                  card.isPercentage 
                    ? card.value >= 15 
                      ? 'text-green-600' 
                      : card.value >= 5 
                        ? 'text-yellow-600' 
                        : 'text-red-600'
                    : card.value >= 0 
                      ? 'text-green-600' 
                      : 'text-red-600'
                }`}
                >
                  {card.isPercentage ? `${card.value.toFixed(1)}%` : formatCurrency(card.value)}
                </p>
              </div>
              {card.value < 0 && !card.isPercentage && (
                <div className="mt-3 flex items-center gap-2 text-red-600">
                  <SafeIcon icon={FiTrendingDown} className="text-sm" />
                  <span className="text-xs font-medium">Loss</span>
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="mt-6 p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-2xl border border-slate-200"
      >
        <div className="flex items-center gap-2 mb-2">
          <SafeIcon icon={FiPercent} className="text-green-600" />
          <span className="font-semibold text-slate-700">Industry Benchmarks</span>
        </div>
        <div className="text-sm text-slate-600 space-y-1">
          <p>• <strong>Excellent:</strong> 15%+ profit margin</p>
          <p>• <strong>Good:</strong> 10-15% profit margin</p>
          <p>• <strong>Average:</strong> 5-10% profit margin</p>
          <p>• <strong>Poor:</strong> Below 5% profit margin</p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ProfitSummary;